import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { clsx } from 'clsx';
import Modal from 'components/Modal/Modal';
import Select from 'components/Select/Select';
import { BUTTON_VARIANT } from 'constants/interfaces';

import { EMAIL } from '../../../constants/formFields';
import { emailValidationSchema } from '../../../utils/validators/MASurveyValidators';
import IconButton from '../../IconButton/IconButton';
import Separator from '../../Separator/Separator';

import type { ReactElement } from 'react';
import type { TEmailValidationSchema } from '../../../utils/validators/MASurveyValidators';
import type IOption from '../../MappingModal/interfaces/IOption';
import type { IShareModalProps } from './interfaces/IShareModal';

import styles from './scss/ShareModal.module.scss';

function ShareModal({
  open,
  onClose,
  onShare,
  vcOptions,
  onShareSurvey,
  currentVcId,
  selectedItems,
  isLoading = false,
}: IShareModalProps): ReactElement {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement | null>(null);
  const [emailList, setEmailList] = useState<string[]>([]);
  const [vcIdList, setVcIdList] = useState<number[]>([]);

  const {
    control,
    reset,
    watch,
    setValue,
    formState: { isValid },
  } = useForm<TEmailValidationSchema>({
    resolver: yupResolver(emailValidationSchema),
    mode: 'onChange',
    defaultValues: {
      [EMAIL]: '',
    },
  });

  const currentEmail = watch(EMAIL);
  const isEmailValid = isValid && currentEmail && currentEmail.trim() !== '';

  const handleClose = () => {
    reset();
    setEmailList([]);
    setVcIdList([]);
    onClose();
  };

  const addEmailToList = () => {
    if (currentEmail && currentEmail.trim() !== '' && !emailList.includes(currentEmail.trim())) {
      setEmailList((prev) => [...prev, currentEmail.trim()]);
      const vcId = vcOptions?.length ? Number(vcOptions[0].value) : 0;
      setVcIdList((prev) => [...prev, vcId]);
      setValue(EMAIL, '');
    }
  };

  const removeEmailFromList = (emailToRemove: string) => {
    const indexToRemove = emailList.findIndex((email) => email === emailToRemove);
    setEmailList((prev) => prev.filter((email) => email !== emailToRemove));
    setVcIdList((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleShare = () => {
    const finalEmailList = [...emailList];

    if (currentEmail && currentEmail.trim() !== '' && !finalEmailList.includes(currentEmail.trim())) {
      finalEmailList.push(currentEmail.trim());
    }
    if (onShareSurvey && selectedItems && currentVcId) {
      onShareSurvey(finalEmailList, currentVcId, selectedItems);
    } else if (onShare) {
      onShare(finalEmailList, vcIdList);
    }
    handleClose();
  };

  return (
    <Modal
      open={open}
      title={t('common.shareModal.title')}
      closeTitle={t('common.cancel')}
      confirmTitle={t('common.shareModal.share')}
      confirmVariant={BUTTON_VARIANT.primary}
      onClose={handleClose}
      onReject={handleClose}
      onConfirm={handleShare}
      isLoading={isLoading}
      disabledConfirm={emailList.length === 0}
      className={styles.modal}
    >
      <div className={styles.container} ref={modalRef}>
        <Separator />
        <div className={styles.formSection}>
          <p className={styles.emailLabel}>
            {t('common.shareModal.emailAddresses')}
          </p>
          <div className={styles.emailInputContainer}>
            <Controller
              name="email"
              control={control}
              render={({ field: controllerField, fieldState }) => (
                <input
                  {...controllerField}
                  type="email"
                  placeholder={t('common.shareModal.emailPlaceholder')}
                  className={clsx(styles.emailInput, {
                    [styles.error]: fieldState.error,
                  })}
                />
              )}
            />
            <IconButton
              size="large"
              icon={<CheckIcon />}
              className={clsx(styles.checkButton, {
                [styles.disabled]: !isEmailValid,
              })}
              disabled={!isEmailValid}
              onClick={addEmailToList}
            />
          </div>
          {emailList.length > 0 && (
            <>
              <div className={styles.listHeader}>
                <p>{t('common.shareModal.listForSending')}</p>
                {vcOptions?.length ? <p>{t('common.shareModal.valueChain')}</p> : null}
              </div>
              <div className={styles.emailList}>
                {emailList.map((email, index) => (
                  <div key={email} className={styles.emailItem}>
                    <span className={styles.emailText}>{email}</span>
                    {vcOptions?.length ? (
                      <div className={styles.selectVC}>
                        <Select
                          placeholder={t('common.selectVC')}
                          options={vcOptions}
                          value={vcIdList[index]
                            ? {
                              value: String(vcIdList[index]),
                              label: vcOptions?.find((option) => option?.value === String(vcIdList[index]))?.label,
                            } : null}
                          onChange={(option) => {
                            const selectedOption = option as IOption | null;
                            setVcIdList((prev) => {
                              const newVcIdList = [...prev];
                              newVcIdList[index] = Number(selectedOption?.value);
                              return newVcIdList;
                            });
                          }}
                          menuPosition="absolute"
                          modalRef={modalRef}
                          maxMenuHeight={200}
                          bottomDirection={index === 1}
                        />
                      </div>
                    ) : null}
                    <IconButton
                      size="small"
                      icon={<CloseIcon />}
                      className={styles.removeButton}
                      onClick={() => removeEmailFromList(email)}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default ShareModal;
